import { Injectable, signal, computed, DestroyRef, inject, effect, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AuthStore } from '../../stores/auth.store';
import { TenantStore } from '../../stores/tenant.store';
import { ANOMALY_PERMISSIONS, ROLES, USER_PERMISSIONS } from '../constants/permissions';
import { LoggerService } from './logger.service';
import { ResponsiveService } from './responsive.service';
import { AppStorageService } from './app-storage.service';
import { MenuFeatureApiService } from '../api/services/menu-feature-api.service';
import type {
  MenuItem as ServerMenuItem,
  MenuSection as ServerMenuSection,
} from '../models/menu.model';
import { AUTO_OPTIMIZATION_SURFACE_ENABLED } from '../config/deployment-surfaces';

export interface MenuChild {
  label: string;
  route: string;
  icon?: string;
  expanded?: boolean;
  active?: boolean;
  children?: MenuChild[];
}

export interface MenuItem {
  icon?: string;
  label: string;
  route?: string;
  children?: MenuChild[];
  expanded?: boolean;
  active?: boolean;
  badge?: string;
  badgeColor?: string;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  visible?: boolean;
}

export interface MenuGroup {
  group: string;
  separator?: boolean;
  active?: boolean;
  items: MenuItem[];
  /**
   * DEV-201 / GAP-008: Tenant entitlement feature required to show this group.
   *
   * Distinct from `MenuItem.requiredRoles` / `requiredPermissions` (RBAC) and
   * from `environment.features.*` (build-time flags) — this gates on the
   * runtime tenant entitlement set (`TenantStore.hasAccessToFeature`). The
   * sidebar binds it through the `*appHasFeature` structural directive so the
   * group is hidden (and re-shown reactively on tenant switch) when the active
   * tenant lacks the entitlement. Groups without a `feature` are always shown.
   */
  feature?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private readonly destroyRef = inject(DestroyRef);
  private authStore = inject(AuthStore);
  private readonly tenantStore = inject(TenantStore);
  private readonly logger = inject(LoggerService);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly appStorage = inject(AppStorageService);
  private readonly menuApi = inject(MenuFeatureApiService);

  // Signal for sidebar visibility
  public showSideBar = signal(true);

  // Signal for mobile menu visibility
  public showMobileMenu = signal(false);

  // Signal for the menu structure
  public pagesMenu = signal<MenuGroup[]>([
    {
      group: 'General',
      separator: false,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/chart-pie.svg',
          label: 'Dashboard',
          route: '/dashboard'
        },
        {
          icon: 'assets/icons/heroicons/outline/collection.svg',
          label: 'Symbols',
          route: '/symbols',
          badge: 'New',
          badgeColor: 'primary',
          requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Data Management',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/database.svg',
          label: 'Schema Management',
          route: '/schema-management',
          requiredPermissions: ['schema:read'],
          requiredRoles: [ROLES.SCHEMA_EDITOR, ROLES.DBA, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/link.svg',
          label: 'Symbol Mapping',
          route: '/admin/symbol-mapping',
          requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/shield-check.svg',
          label: 'Data Quality',
          route: '/data-quality',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/refresh.svg',
          label: 'Exchanges',
          route: '/exchanges',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Trading',
      separator: true,
      // DEV-201 / GAP-008: gated on the tenant 'trading' entitlement.
      feature: 'trading',
      items: [
        {
          icon: 'assets/icons/heroicons/outline/code.svg',
          label: 'Strategy Builder',
          route: '/strategy-builder',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/collection.svg',
          label: 'Strategies',
          route: '/strategies',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/trending-up.svg',
          label: 'Trading',
          route: '/trading',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/trending-up.svg',
          label: 'Backtests',
          route: '/backtests',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/folder.svg',
          label: 'Order Book',
          route: '/order-book',
          requiredRoles: [ROLES.TRADER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/currency-dollar.svg',
          label: 'Market Making',
          route: '/market-making',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/cube.svg',
          label: 'Feature Sets',
          route: '/features/feature-sets',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Risk Management',
      separator: true,
      // DEV-201 / GAP-008: gated on the tenant 'risk-management' entitlement.
      feature: 'risk-management',
      items: [
        {
          icon: 'assets/icons/heroicons/outline/shield-check.svg',
          label: 'Risk Management',
          route: '/risk-management',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/shield-exclamation.svg',
          label: 'Risk Controls',
          route: '/risk-controls',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/adjustments-horizontal.svg',
          label: 'Risk Profiles',
          route: '/risk-profiles',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        ...(AUTO_OPTIMIZATION_SURFACE_ENABLED
          ? [{
              icon: 'assets/icons/heroicons/outline/cog-6-tooth.svg',
              label: 'Auto Optimization',
              route: '/auto-optimization',
              requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
            }]
          : []),
        {
          icon: 'assets/icons/heroicons/outline/arrow-trending-down.svg',
          label: 'Auto De-Risking',
          route: '/auto-de-risking',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/chart-pie.svg',
          label: 'P&L Analytics',
          route: '/pnl-analytics',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/presentation-chart-line.svg',
          label: 'P&L Dashboard',
          route: '/pl-dashboard',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/currency-dollar.svg',
          label: 'Net Profitability',
          route: '/profitability',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'HFT & Performance',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/trending-up.svg',
          label: 'HFT Metrics',
          route: '/hft-metrics',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        },
        {
          icon: 'assets/icons/heroicons/outline/magnifying-glass.svg',
          label: 'Order Diagnostics',
          route: '/order-diagnostics',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER]
        },
        {
          icon: 'assets/icons/heroicons/outline/chart-pie.svg',
          label: 'Performance Monitoring',
          route: '/performance-monitoring',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        },
        {
          icon: 'assets/icons/heroicons/outline/exclamation-triangle.svg',
          label: 'Heat Maps',
          route: '/heat-maps',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER]
        }
      ]
    },
    {
      group: 'ML & Analytics',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/cube.svg',
          label: 'ML Anomaly Detection',
          route: '/ml-anomaly-detection',
          // Gate mirrors the /ml-anomaly-detection route guard exactly
          // (app.routes.ts uses permissionGuard: requiredRoles OR
          // requiredPermissions). tenant-viewer holds anomaly:view by
          // default, so a roles-only menu gate hid the item from principals
          // the guard admits (navigable page, invisible nav entry).
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN, ROLES.TENANT_OPERATOR],
          requiredPermissions: [ANOMALY_PERMISSIONS.VIEW]
        },
        {
          icon: 'assets/icons/heroicons/outline/chart-bar.svg',
          label: 'ML Monitoring',
          route: '/ml-monitoring',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/cube.svg',
          label: 'Innovation Control',
          route: '/innovation-control',
          requiredPermissions: ['allocation:read']
        }
      ]
    },
    {
      group: 'Operations',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/cog.svg',
          label: 'Predictive Maintenance',
          route: '/predictive-maintenance',
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Compliance & Reporting',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/document-report.svg',
          label: 'Regulatory Reporting',
          route: '/regulatory-reporting',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/users.svg',
          label: 'Client Portal',
          route: '/client-portal',
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/credit-card.svg',
          label: 'Client Accounts',
          route: '/client-accounts',
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/eye.svg',
          label: 'Audit Logs',
          route: '/admin/audit',
          requiredRoles: [ROLES.TENANT_VIEWER, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Configuration',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/cog.svg',
          label: 'Environment Config',
          route: '/config',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/cog.svg',
          label: 'Config Management',
          route: '/config-management',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/database.svg',
          label: 'Multi-Tenant',
          route: '/multi-tenant',
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        },
      ]
    },
    {
      group: 'Developer Tools',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/code.svg',
          label: 'API Management',
          route: '/api-management',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Administration',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/users.svg',
          label: 'Users',
          route: '/users',
          requiredPermissions: [USER_PERMISSIONS.READ],
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/office-building.svg',
          label: 'Tenants',
          route: '/tenants',
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/key.svg',
          label: 'Permissions',
          route: '/permissions-management',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          icon: 'assets/icons/heroicons/outline/shield-check.svg',
          label: 'Control Panel',
          route: '/admin/control-panel',
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      group: 'Monitoring',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/heart.svg',
          label: 'System Health',
          route: '/system-health',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        },
        {
          icon: 'assets/icons/heroicons/outline/document-report.svg',
          label: 'Reports',
          route: '/reports',
          children: [
            { label: 'All Reports', route: '/reports' },
            { label: 'Daily Desk Report', route: '/reports/daily-desk' }
          ]
        }
      ]
    },
    {
      group: 'Settings',
      separator: true,
      items: [
        {
          icon: 'assets/icons/heroicons/outline/cog.svg',
          label: 'Settings',
          route: '/settings'
        },
        {
          icon: 'assets/icons/heroicons/outline/information-circle.svg',
          label: 'Help',
          route: '/help'
        }
      ]
    }
  ]);

  /**
   * GAP-079 residual · Pristine snapshot of the vetted static menu, captured
   * once at construction. Every server overlay starts from THIS copy — not
   * from the current `pagesMenu()` value — so a re-fetch (fresh login after
   * an unauthenticated bootstrap, user switch, tenant switch) can never
   * compound a previous overlay's hidden flags or RBAC adoption.
   */
  private readonly staticDefaultGroups: MenuGroup[] = this.pagesMenu();

  /**
   * GAP-079 residual · Identity (user + active tenant) the server menu was
   * last fetched for. `null` = never fetched (or last seen unauthenticated).
   */
  private lastMenuAuthKey: string | null = null;

  // Computed signal to check if a menu item should be visible based on permissions
  // Uses OR logic: visible if user has ANY required role OR ANY required permission
  // This aligns with the permissionGuard in role.guard.ts which uses the same OR logic
  private isMenuItemVisible = computed(() => (item: MenuItem): boolean => {
    // "No gate" = neither requiredRoles NOR requiredPermissions constrains the
    // item, treating an EMPTY array the SAME as absent. The /admin/menu model
    // documents `Empty ⇒ no gate` (and serde omits empty vecs), so a
    // server-driven item with `requiredRoles: []` must be visible to all.
    // Without collapsing empty→absent, such an item would skip the early
    // return, fail both `length > 0` checks, and fall through to `false` —
    // hidden from EVERY user (GAP-079 review MEDIUM).
    const roleGate = item.requiredRoles ?? [];
    const permGate = item.requiredPermissions ?? [];
    if (roleGate.length === 0 && permGate.length === 0) {
      return true;
    }

    // OR logic: visible if the user has ANY required role OR ANY required
    // permission (aligns with permissionGuard in role.guard.ts).
    if (roleGate.length > 0 && this.authStore.hasAnyRole()(roleGate)) {
      return true;
    }
    if (permGate.length > 0 && this.authStore.hasAnyPermission()(permGate)) {
      return true;
    }

    // User has neither a required role nor a required permission.
    return false;
  });

  // Computed signal to get filtered menu based on permissions
  public visibleMenu = computed(() => {
    const menu = this.pagesMenu();
    const isVisible = this.isMenuItemVisible();

    return menu.map(group => ({
      ...group,
      // GAP-079 residual: `visible === false` marks items the server-shaped
      // /admin/menu payload omitted for this caller (see overlayServerMenu).
      // Static items never set `visible`, so the static path is unaffected.
      // The RBAC check still runs on adopted items — it uses the SAME
      // OR-semantics as the server's `item_visible` (menu.rs), so it is a
      // reactive re-application (e.g. on logout) of the server's decision,
      // not a conflicting second filter.
      items: group.items.filter(item => item.visible !== false && isVisible(item))
    })).filter(group => group.items.length > 0); // Remove empty groups
  });

  // Computed signal to get all flat menu items
  public flatItems = computed(() => {
    const menu = this.visibleMenu();
    return menu.flatMap(group =>
      group.items.flatMap(item => {
        const items: Array<MenuItem | MenuChild> = [item];
        if (item.children) {
          items.push(...item.children);
        }
        return items;
      })
    );
  });

  constructor() {
    // Initialize from localStorage if available
    this.loadSidebarState();

    // Track route changes to update active menu items
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updateActiveMenuItems();
      this.closeMobileMenu();
    });

    // GAP-015: Auto-close mobile menu when viewport transitions to desktop.
    // The service owns the showMobileMenu state, so it owns this invariant.
    let wasDesktop = this.responsiveService.isDesktop();
    effect(() => {
      const isDesktop = this.responsiveService.isDesktop();
      if (isDesktop && !wasDesktop) {
        this.closeMobileMenu();
      }
      wasDesktop = isDesktop;
    });

    // GAP-079 residual: make the sidebar genuinely server-driven. The
    // /admin/menu payload is bearer-authed and caller-shaped (RBAC-filtered
    // server-side), so fetch it only once a session exists and RE-FETCH
    // whenever the authenticated PRINCIPAL changes — and the principal is
    // (user, active tenant), not just the user: the request is stamped with
    // X-Tenant-Id (tenant.interceptor resolves
    // `tenantStore.currentTenant()?.id ?? user.tenantId`) and the caller's
    // effective roles/permissions are per-tenant (switchTenant() re-runs
    // loadEffectivePermissions). Keying on the user id alone left a
    // tenant SWITCH serving the PREVIOUS tenant's overlay, whose
    // `visible:false` flags permanently hid nav items the new tenant's
    // roles entitle (visibleMenu short-circuits on `visible !== false`
    // before the reactive RBAC check). The previous implementation
    // fetched exactly once in the constructor: on an unauthenticated
    // bootstrap that call could only 401 into the static fallback and was
    // never retried after login, leaving server-driven shaping inert for the
    // whole session.
    effect(() => {
      let authKey: string | null = null;
      if (this.authStore.isAuthenticated()) {
        const user = this.authStore.user();
        // Same tenant resolution order as tenant.interceptor, so the key
        // tracks exactly what the outgoing request will be scoped to. Both
        // signals are read INSIDE the effect: a tenant switch (TenantStore
        // currentTenant change) re-runs it and triggers a fresh fetch.
        const tenantId =
          this.tenantStore.currentTenant()?.id ?? user?.tenantId ?? 'no-tenant';
        authKey = `${user?.id ?? 'authenticated'}::${tenantId}`;
      }
      if (authKey === null) {
        // Logged out: clear the key so the NEXT login (even by the same
        // user, whose server-side roles may have changed meanwhile) fetches
        // a fresh caller-shaped menu.
        this.lastMenuAuthKey = null;
      } else if (authKey !== this.lastMenuAuthKey) {
        this.lastMenuAuthKey = authKey;
        // Capture into a const so the string narrowing survives the closure
        // (the fetch carries the key it was issued under — see loadServerMenu).
        const issuedKey = authKey;
        // untracked: the fetch/overlay writes pagesMenu — do not let those
        // signal reads/writes become dependencies of this auth-watch effect.
        untracked(() => this.loadServerMenu(issuedKey));
      }
    });
  }

  /**
   * GAP-079 residual · Server-driven navigation with a zero-regression
   * safety net.
   *
   * The sidebar renders `pagesMenu()` — a MenuGroup[] whose visual identity
   * (heroicon SVG asset paths, tenant-entitlement `feature` gating, and the
   * Reports submenu `children`) is NOT carried by the server payload
   * (`MenuSection[]`, which uses icon-class strings, is flat, and has
   * no `feature` field). Adopting the raw server shape would therefore drop
   * `feature` gating, drop submenu children, and break every icon.
   *
   * So the contract is: the server is authoritative for WHO sees each item
   * (`GET /admin/menu` returns the caller-filtered subset, with each item's
   * `requiredRoles` / `requiredPermissions` metadata); the vetted static
   * menu is authoritative for HOW it renders (icons, children, badges, group
   * entitlement, ordering). `overlayServerMenu()` merges the two by ROUTE:
   * static items present in the payload adopt the server's RBAC metadata,
   * static items ABSENT from the payload are hidden (the server filtered
   * them out for this caller). On empty payload, backend error (handled
   * inside getMenu() — one warn log, static fallback), or a payload sharing
   * no routes with the static menu, we keep the static menu untouched — the
   * nav is never blanked and never re-ordered.
   *
   * (The previous implementation adopted the server menu only on an EXACT
   * (title, order, route) topology match — a condition that could never
   * hold, because the server intentionally names its canonical sections
   * differently AND drops caller-invisible items/sections, so server-driven
   * shaping was inert for every caller. Route-keyed overlay fires for any
   * caller whose payload overlaps the static menu.)
   *
   * Ordering guard (`authKey`): each fetch is stamped with the (user,
   * tenant) key it was issued under, and the handlers drop the response
   * when `lastMenuAuthKey` has moved on. Without this, concurrent fetches
   * race: the auth-watch effect re-fires when (user.id, activeTenantId)
   * changes, and on a real bootstrap AuthStore hydrates BEFORE TenantStore's
   * async switch lands — so fetch#1 (default tenant) can resolve AFTER
   * fetch#2 (active tenant) and its stale overlay would win permanently
   * (`visible:false` hiding items the active tenant entitles). A logout
   * mid-flight clears `lastMenuAuthKey` to null, which likewise drops any
   * in-flight response.
   */
  private loadServerMenu(authKey: string): void {
    // Always overlay onto the pristine construction-time static default so
    // repeated fetches (login, user switch, tenant switch) start from a
    // clean slate.
    const staticGroups = this.staticDefaultGroups;

    this.menuApi.getMenu()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sections) => {
          if (this.lastMenuAuthKey !== authKey) {
            // Out-of-order response from a superseded (user, tenant) key —
            // a newer fetch owns the overlay now (or the user logged out).
            this.logger.info(
              'Dropped stale /admin/menu response (auth key superseded)',
              { component: 'MenuService' }
            );
            return;
          }
          const merged = this.overlayServerMenu(sections, staticGroups);
          if (merged) {
            this.setMenu(merged);
            // Re-apply active/expanded state for the current route.
            this.updateActiveMenuItems();
            this.logger.info(
              'Sidebar menu adopted server-driven shaping (route-keyed overlay)',
              {
                component: 'MenuService',
                groups: merged.length,
                hiddenItems: merged.reduce(
                  (n, g) => n + g.items.filter(i => i.visible === false).length,
                  0
                ),
              }
            );
          } else {
            this.logger.warn(
              'Server menu shares no routes with the vetted static menu; ' +
                'keeping static navigation (no regression)',
              { component: 'MenuService', serverSections: sections.length }
            );
          }
        },
        error: () => {
          if (this.lastMenuAuthKey !== authKey) {
            // Stale fetch (superseded key) failing is a non-event — a newer
            // fetch owns the overlay; don't emit a misleading warning.
            return;
          }
          // getMenu() already swallows backend errors into a static fallback,
          // so this is a defensive belt-and-braces: keep the static menu.
          this.logger.warn(
            'Menu load failed unexpectedly; keeping static navigation',
            { component: 'MenuService' }
          );
        },
      });
  }

  /**
   * GAP-079 residual · Route-keyed overlay of the caller-shaped server menu
   * onto the vetted static menu. Returns the merged MenuGroup[] or `null`
   * to signal "keep the static menu".
   *
   * Semantics per static item:
   *   - route PRESENT in the payload → keep the static visual identity
   *     (icon, children, badge, badgeColor, group `feature`) and adopt the
   *     server's `requiredRoles` / `requiredPermissions` as-is. An omitted
   *     or empty server gate means "no gate" per the /admin/menu model, and
   *     is handled by isMenuItemVisible (empty ⇒ visible-to-all); falling
   *     back to the static gate would silently re-restrict an item the
   *     server intends to ungate (GAP-079 review MEDIUM).
   *   - route ABSENT from the payload → `visible: false`. The server
   *     already authz-filtered the payload for this caller (menu.rs
   *     `item_visible` + empty-section drop), so absence is an
   *     authoritative "this caller may not see it". Note the client-side
   *     fallback payload (buildDefaultMenu, served on backend error/empty)
   *     contains every static route, so the fallback path hides nothing.
   *
   * Safety net: if the payload shares ZERO routes with the static menu it is
   * foreign/incompatible (a real /admin/menu response always contains the
   * ungated Dashboard/Settings/Help entries for any authenticated caller) —
   * return `null` so the nav is never blanked.
   */
  private overlayServerMenu(
    sections: ServerMenuSection[],
    staticGroups: MenuGroup[]
  ): MenuGroup[] | null {
    if (!Array.isArray(sections) || sections.length === 0) {
      return null;
    }

    // Flatten the payload into a route-keyed lookup (recursing into nested
    // children, which normalizeItem preserves when a server response nests).
    const byRoute = new Map<string, ServerMenuItem>();
    const collect = (items: readonly ServerMenuItem[]): void => {
      for (const serverItem of items) {
        if (serverItem.route) {
          byRoute.set(serverItem.route, serverItem);
        }
        if (serverItem.children?.length) {
          collect(serverItem.children);
        }
      }
    };
    for (const section of sections) {
      collect(section.items);
    }

    const matchedCount = staticGroups.reduce(
      (n, g) => n + g.items.filter(it => it.route && byRoute.has(it.route)).length,
      0
    );
    if (matchedCount === 0) {
      return null;
    }

    return staticGroups.map(group => ({
      ...group,
      items: group.items.map(staticItem => {
        const serverItem = staticItem.route
          ? byRoute.get(staticItem.route)
          : undefined;
        if (!serverItem) {
          return { ...staticItem, visible: false };
        }
        return {
          ...staticItem,
          visible: true,
          requiredRoles: serverItem.requiredRoles,
          requiredPermissions: serverItem.requiredPermissions,
        };
      }),
    }));
  }

  private loadSidebarState(): void {
    // GAP-FE-C013: SSR-safe wrapper; preserves the 'sidebarCollapsed' key.
    const savedState = this.appStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      this.showSideBar.set(savedState === 'false' ? false : true);
    }
  }

  private saveSidebarState(): void {
    // GAP-FE-C013: SSR-safe wrapper; preserves the 'sidebarCollapsed' key.
    this.appStorage.setItem('sidebarCollapsed', this.showSideBar().toString());
  }

  /**
   * Toggle sidebar visibility
   */
  public toggleSidebar(): void {
    this.showSideBar.update(v => !v);
    this.saveSidebarState();
  }

  /**
   * Toggle mobile menu visibility
   */
  public toggleMobileMenu(): void {
    this.showMobileMenu.update(v => !v);
  }

  /**
   * Set the menu structure
   */
  public setMenu(groups: MenuGroup[]): void {
    this.pagesMenu.set(groups);
  }

  /**
   * Add a menu group
   */
  public addMenuGroup(group: MenuGroup): void {
    this.pagesMenu.update(current => [...current, group]);
  }

  /**
   * Remove a menu group by name
   */
  public removeMenuGroup(groupName: string): void {
    this.pagesMenu.update(current =>
      current.filter(g => g.group !== groupName)
    );
  }

  /**
   * Update a specific menu item
   */
  public updateMenuItem(route: string, updates: Partial<MenuItem>): void {
    this.pagesMenu.update(groups =>
      groups.map(group => ({
        ...group,
        items: group.items.map(item =>
          item.route === route ? { ...item, ...updates } : item
        )
      }))
    );
  }

  /**
   * Toggle expanded state for a menu item with children
   */
  public toggleMenuItemExpanded(route: string): void {
    this.pagesMenu.update(groups =>
      groups.map(group => ({
        ...group,
        items: group.items.map(item =>
          item.route === route
            ? { ...item, expanded: !item.expanded }
            : item
        )
      }))
    );
  }

  /**
   * Close mobile menu
   */
  public closeMobileMenu(): void {
    this.showMobileMenu.set(false);
  }

  /**
   * Open mobile menu
   */
  public openMobileMenu(): void {
    this.showMobileMenu.set(true);
  }

  /**
   * Check if a route is active
   */
  public isActive(instruction: string): boolean {
    return this.router.isActive(this.router.createUrlTree([instruction]), {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  /**
   * Update active menu items based on current route
   */
  private updateActiveMenuItems(): void {
    this.pagesMenu.update(groups =>
      groups.map(group => {
        let activeGroup = false;
        const items = group.items.map(item => {
          const active = item.route ? this.isActive(item.route) : false;
          const expanded = active || (item.expanded ?? false);

          if (active) {
            activeGroup = true;
          }

          // Recursively expand children based on active route
          if (item.children) {
            this.expand(item.children);
            const activeChild = item.children.some(child => this.isActive(child.route));
            if (activeChild) {
              activeGroup = true;
              return { ...item, active: true, expanded: true };
            }
          }

          return { ...item, active, expanded };
        });

        return { ...group, items, active: activeGroup };
      })
    );
  }

  /**
   * Toggle menu item (for items with children)
   * Only processes items that have children to avoid unnecessary signal updates
   * that could interfere with routerLink navigation
   */
  public toggleMenu(item: MenuItem): void {
    // Only toggle expansion for items with children
    // Items without children use routerLink directly and don't need this
    if (!item.children) {
      return;
    }

    // Expand sidebar when toggling menu
    this.showSideBar.set(true);

    // Collapse all other menus except the selected one
    this.pagesMenu.update(groups =>
      groups.map(group => ({
        ...group,
        items: group.items.map(menuItem =>
          menuItem === item
            ? { ...menuItem, expanded: !menuItem.expanded }
            : { ...menuItem, expanded: false }
        )
      }))
    );
  }

  /**
   * Toggle submenu item (for nested children)
   */
  public toggleSubMenu(submenu: MenuChild): void {
    submenu.expanded = !submenu.expanded;
  }

  /**
   * Recursively expand menu items based on active route
   */
  private expand(items: Array<MenuChild>): void {
    items.forEach((item) => {
      item.expanded = this.isActive(item.route);
      if (item.children) {
        this.expand(item.children);
      }
    });
  }

}
